// The gate of the web: the office and the end customer's portal (D-172,
// D-240, D-290, INC-12c-1).
//
// The field has its own gate in App.tsx; this one stands before the routes
// the other two roles reach — the office's sections for the administration,
// the portal for the end customer — and decides the same way for the states
// a session can be in: a forced change of password, a revoked device and a
// suspended organization go where they go in the field. What differs is the
// session that ended because the server no longer takes it (D-290): the
// office passes the person through the sign in with the reason, and the sign
// in brings them back to the route they were on. A sign out passes nothing:
// it starts over.
//
// INC-12c-1 (A.1): an address of the portal opened with no session at all —
// the link of a mail, a bookmark — passes through the sign in too, without
// the reason, and comes back to it once the person is in.
//
// The office's code and the portal's are loaded here and nowhere else, each
// for its own role (D-83): a person of the portal never downloads the office,
// and one of the office never downloads the portal.
import { lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';
import { FIELD_ROLE } from './fieldRuntime';
import { useSession } from './hooks';
import { homeOf, type SectionKey } from './sections';

const OfficeRoot = lazy(async () =>
  import('../office/entry').then((module) => ({ default: module.OfficeRoot })),
);
const OfficeHelp = lazy(async () => (await import('../office/entry')).loadOfficeHelp());
const PortalRoot = lazy(async () =>
  import('../portal/entry').then((module) => ({ default: module.PortalRoot })),
);

/** The two experiences of the web, each the one role's (D-172). */
export type Experience = 'office' | 'portal';

/** What the sign in receives from this gate: where the person was, to come
 *  back to it, and whether the server ended their session (D-290) or there
 *  was none. */
export interface SignInReturn {
  from: string;
  expired: boolean;
}

export function WebGate({
  experience,
  section,
  help = false,
}: {
  experience: Experience;
  /** The office's section; none for the portal. */
  section?: SectionKey;
  /** INC-15 (D-401): the office's help, which is no section of its menu. */
  help?: boolean;
}) {
  const { t } = useTranslation();
  const snapshot = useSession();
  const location = useLocation();
  switch (snapshot.status) {
    case 'loading':
      return <p className="app-main muted">{t('app.loading')}</p>;
    case 'anonymous': {
      const from = `${location.pathname}${location.search}${location.hash}`;
      // The office comes back only from a session the server ended; the
      // portal from any address asked for without one (A.1).
      const state: SignInReturn | null =
        snapshot.expired || experience === 'portal' ? { from, expired: snapshot.expired } : null;
      return <Navigate to="/login" replace state={state} />;
    }
    case 'password_change_required':
      return <Navigate to="/password" replace />;
    case 'revoked':
    case 'suspended':
      return <Navigate to="/status" replace />;
    case 'authenticated': {
      const profile = snapshot.profile;
      if (profile === null) return <p className="app-main muted">{t('app.loading')}</p>;
      const role = profile.role;
      if (role === FIELD_ROLE) return <Navigate to="/" replace />;
      if (experience === 'portal') {
        if (role !== 'client') return <Navigate to={homeOf(role, profile.modules)} replace />;
        return <PortalRoot />;
      }
      if (role !== 'admin') return <Navigate to={homeOf(role, profile.modules)} replace />;
      if (help) return <OfficeHelp />;
      if (section === undefined) return <Navigate to={homeOf(role, profile.modules)} replace />;
      return <OfficeRoot section={section} />;
    }
  }
}
